import type { ConsultationField, ConsultationFieldKind } from '../lib/api.ts';

/**
 * Rendering the consultation form.
 *
 * One component, used by the client's page and by the preview in the form
 * builder. A preview that renders differently from the real thing is worse than
 * no preview, because it is the thing the studio checks its wording against.
 *
 * Every answer is held as an array of strings whatever the kind. Only a multi
 * ever has more than one, but a single shape means the page has no per-kind
 * state handling and hydrating a part-filled form is one assignment.
 */

export type AnswerMap = Record<number, string[]>;

const SCALE_POINTS = [1, 2, 3, 4, 5];

/** Kinds the client reads rather than answers. */
export function isContentKind(kind: ConsultationFieldKind): boolean {
  return kind === 'heading' || kind === 'statement';
}

/** Start with what was submitted before, or empty. */
export function initialAnswers(
  fields: ConsultationField[],
  previous?: Array<{ fieldId: number; values: string[] }> | null,
): AnswerMap {
  const answers: AnswerMap = {};
  for (const field of fields) {
    if (!isContentKind(field.kind)) answers[field.id] = [];
  }
  for (const answer of previous ?? []) {
    if (answer.fieldId in answers) answers[answer.fieldId] = answer.values;
  }
  return answers;
}

/** The payload the API expects: a multi sends its array, everything else one string. */
export function toSubmission(
  fields: ConsultationField[],
  answers: AnswerMap,
): Array<{ fieldId: number; value: string | string[] }> {
  return fields
    .filter((field) => !isContentKind(field.kind))
    .map((field) => {
      const values = answers[field.id] ?? [];
      return {
        fieldId: field.id,
        value: field.kind === 'multi' ? values : (values[0] ?? ''),
      };
    })
    // An unanswered optional question is left out entirely rather than sent as
    // an empty string, so the stored form holds only what was actually said.
    .filter((entry) => (Array.isArray(entry.value) ? entry.value.length > 0 : entry.value !== ''));
}

/** How many questions have something in them. Drives the progress line. */
export function answeredCount(fields: ConsultationField[], answers: AnswerMap): number {
  return fields.filter(
    (field) => !isContentKind(field.kind) && (answers[field.id]?.length ?? 0) > 0,
  ).length;
}

/**
 * One stored answer, as a person should read it.
 *
 * Only dates need translating: they are stored as YYYY-MM-DD because that is
 * what a date input produces and what sorts correctly, and nobody wants to read
 * their own birthday that way.
 */
export function formatAnswer(kind: ConsultationFieldKind, value: string): string {
  if (kind !== 'date') return value;
  const parsed = new Date(`${value}T12:00:00Z`);
  if (Number.isNaN(parsed.getTime())) return value;
  return new Intl.DateTimeFormat('en-GB', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(parsed);
}

const TEXT_INPUT_TYPE: Partial<Record<ConsultationFieldKind, string>> = {
  text: 'text',
  email: 'email',
  tel: 'tel',
  date: 'date',
  number: 'number',
};

const AUTOCOMPLETE: Partial<Record<ConsultationFieldKind, string>> = {
  email: 'email',
  tel: 'tel',
};

interface FieldProps {
  field: ConsultationField;
  values: string[];
  error: string | null;
  disabled: boolean;
  onChange: (values: string[]) => void;
}

function Field({ field, values, error, disabled, onChange }: FieldProps) {
  const inputId = `cf-${field.id}`;
  const helpId = field.helpText ? `${inputId}-help` : undefined;
  const errorId = error ? `${inputId}-error` : undefined;
  // Both, when both exist: a screen reader should get the guidance and the
  // problem, not whichever happened to be written last.
  const describedBy = [helpId, errorId].filter(Boolean).join(' ') || undefined;

  const help = field.helpText ? (
    <p className="field-hint" id={helpId}>
      {field.helpText}
    </p>
  ) : null;
  const problem = error ? (
    <p className="field-error" id={errorId}>
      {error}
    </p>
  ) : null;

  if (field.kind === 'heading') {
    return <h2 className="cform__heading">{field.label}</h2>;
  }

  if (field.kind === 'statement') {
    return <p className="cform__statement">{field.label}</p>;
  }

  const required = field.isRequired ? (
    <span className="cform__required" aria-hidden="true">
      required
    </span>
  ) : (
    <span className="cform__optional">optional</span>
  );

  if (field.kind === 'consent') {
    const ticked = values[0] === 'Agreed';
    return (
      <div className={`field cform__field cform__field--consent ${error ? 'cform__field--bad' : ''}`}>
        <label className="cform__tick" htmlFor={inputId}>
          <input
            id={inputId}
            type="checkbox"
            checked={ticked}
            disabled={disabled}
            required={field.isRequired}
            aria-invalid={error ? true : undefined}
            aria-describedby={describedBy}
            onChange={(event) => onChange(event.target.checked ? ['Agreed'] : [])}
          />
          <span>{field.label}</span>
        </label>
        {help}
        {problem}
      </div>
    );
  }

  // Radio and checkbox groups: a fieldset and a legend, because a group of
  // inputs sharing one question is exactly what those elements are for.
  if (field.kind === 'yesno' || field.kind === 'choice' || field.kind === 'multi') {
    const options = field.kind === 'yesno' ? ['Yes', 'No'] : field.options;
    const multiple = field.kind === 'multi';

    return (
      <fieldset className={`cform__field cform__group ${error ? 'cform__field--bad' : ''}`}>
        <legend className="cform__legend">
          {field.label} {required}
        </legend>
        {help}
        <div
          className={`cform__options ${field.kind === 'yesno' ? 'cform__options--inline' : ''}`}
          aria-describedby={describedBy}
        >
          {options.map((option) => {
            const optionId = `${inputId}-${option.replace(/\W+/g, '-').toLowerCase()}`;
            const checked = values.includes(option);
            return (
              <label key={option} className="cform__option" htmlFor={optionId}>
                <input
                  id={optionId}
                  type={multiple ? 'checkbox' : 'radio'}
                  name={inputId}
                  value={option}
                  checked={checked}
                  disabled={disabled}
                  aria-invalid={error ? true : undefined}
                  onChange={(event) => {
                    if (!multiple) {
                      onChange([option]);
                      return;
                    }
                    onChange(
                      event.target.checked
                        ? [...values, option]
                        : values.filter((value) => value !== option),
                    );
                  }}
                />
                <span>{option}</span>
              </label>
            );
          })}
        </div>
        {problem}
      </fieldset>
    );
  }

  if (field.kind === 'scale') {
    const low = field.options[0];
    const high = field.options[1];

    return (
      <fieldset className={`cform__field cform__group ${error ? 'cform__field--bad' : ''}`}>
        <legend className="cform__legend">
          {field.label} {required}
        </legend>
        {help}
        <div className="cform__scale" aria-describedby={describedBy}>
          <div className="cform__scale-points">
            {SCALE_POINTS.map((point) => {
              const optionId = `${inputId}-${point}`;
              return (
                <label key={point} className="cform__scale-point" htmlFor={optionId}>
                  <input
                    id={optionId}
                    type="radio"
                    name={inputId}
                    value={point}
                    checked={values[0] === String(point)}
                    disabled={disabled}
                    aria-invalid={error ? true : undefined}
                    onChange={() => onChange([String(point)])}
                  />
                  <span>{point}</span>
                </label>
              );
            })}
          </div>
          {(low || high) && (
            <div className="cform__scale-ends">
              <span className="cform__scale-end">{low ?? ''}</span>
              <span className="cform__scale-end">{high ?? ''}</span>
            </div>
          )}
        </div>
        {problem}
      </fieldset>
    );
  }

  if (field.kind === 'textarea') {
    return (
      <div className={`field cform__field ${error ? 'cform__field--bad' : ''}`}>
        <label htmlFor={inputId}>
          {field.label} {required}
        </label>
        {help}
        <textarea
          id={inputId}
          className="textarea"
          rows={4}
          maxLength={4000}
          value={values[0] ?? ''}
          disabled={disabled}
          required={field.isRequired}
          aria-invalid={error ? true : undefined}
          aria-describedby={describedBy}
          onChange={(event) => onChange(event.target.value ? [event.target.value] : [])}
        />
        {problem}
      </div>
    );
  }

  return (
    <div className={`field cform__field ${error ? 'cform__field--bad' : ''}`}>
      <label htmlFor={inputId}>
        {field.label} {required}
      </label>
      {help}
      <input
        id={inputId}
        type={TEXT_INPUT_TYPE[field.kind] ?? 'text'}
        className="input"
        value={values[0] ?? ''}
        disabled={disabled}
        required={field.isRequired}
        autoComplete={AUTOCOMPLETE[field.kind]}
        maxLength={field.kind === 'text' ? 300 : undefined}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy}
        onChange={(event) => onChange(event.target.value ? [event.target.value] : [])}
      />
      {problem}
    </div>
  );
}

interface ConsultationFieldsProps {
  fields: ConsultationField[];
  answers: AnswerMap;
  errors?: Record<number, string>;
  disabled?: boolean;
  onChange: (fieldId: number, values: string[]) => void;
}

export function ConsultationFields({
  fields,
  answers,
  errors = {},
  disabled = false,
  onChange,
}: ConsultationFieldsProps) {
  return (
    <>
      {fields.map((field) => (
        <Field
          key={field.id}
          field={field}
          values={answers[field.id] ?? []}
          error={errors[field.id] ?? null}
          disabled={disabled}
          onChange={(values) => onChange(field.id, values)}
        />
      ))}
    </>
  );
}
